import type { UseChatHelpers } from '@ai-sdk/react'
import { RotateCcwIcon, SendHorizontalIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Markdown } from '@/components/ui/Markdown'
import { GettingStartedHelpLink } from '@/modules/getting-started'
import { messageSources, messageText } from '../messageParts'
import type { HelpUIMessage, Surface } from '../types'
import { helpMarkdownComponents } from './markdownComponents'
import { SourceList } from './SourceList'

interface AnswerPaneProps {
  surface: Surface
  messages: HelpUIMessage[]
  status: UseChatHelpers<HelpUIMessage>['status']
  error: UseChatHelpers<HelpUIMessage>['error']
  onAsk: (question: string) => void
  onOpenArticle: (slug: string) => void
  activeSlug: string | null
  onSelectSource: (slug: string) => void
  onStartOver: () => void
}

export function AnswerPane({
  surface,
  messages,
  status,
  error,
  onAsk,
  onOpenArticle,
  activeSlug,
  onSelectSource,
  onStartOver,
}: AnswerPaneProps) {
  const hasConversation = messages.length > 0
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const linkComponents = helpMarkdownComponents(onOpenArticle)

  const isBusy = status === 'submitted' || status === 'streaming'
  const lastMessage = messages[messages.length - 1]
  const isThinking =
    isBusy &&
    (lastMessage?.role !== 'assistant' || messageText(lastMessage) === '')

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll to the latest content when messages or the thinking state change.
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [messages, isThinking])

  const submit = (question: string) => {
    const trimmed = question.trim()
    if (!trimmed || isBusy) return
    onAsk(trimmed)
    setInput('')
  }

  return (
    <div className="flex h-full min-h-0 flex-col border-b border-border md:border-b-0 md:border-r">
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5">
        {messages.length === 0 ? (
          <div className="pt-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-4">
              Ask CounselPro
            </p>
            <p className="mt-1 text-[0.95rem] font-medium text-blue-7 text-pretty">
              Help with {surface.label}
            </p>
            <div className="mt-4 flex flex-col gap-2">
              {surface.suggestions.map((question) => (
                <button
                  key={question}
                  type="button"
                  onClick={() => submit(question)}
                  className="cursor-pointer rounded-xl border border-border bg-white px-3.5 py-2.5 text-left text-sm text-blue-7 transition-colors hover:border-brand/40 hover:bg-brand/5 text-pretty"
                >
                  {question}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-blue-4">
              or type your own question above
            </p>
            <div className="mt-6">
              <GettingStartedHelpLink />
            </div>
          </div>
        ) : (
          messages.map((message) =>
            message.role === 'user' ? (
              <div key={message.id} className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-blue-2 px-3.5 py-2.5 text-[0.94rem] text-blue-7 text-pretty">
                  {messageText(message)}
                </div>
              </div>
            ) : (
              <div key={message.id} className="text-[0.94rem]">
                <Markdown
                  content={messageText(message)}
                  componentsOverride={linkComponents}
                />
                {!(isBusy && message.id === lastMessage?.id) && (
                  <SourceList
                    sources={messageSources(message)}
                    activeSlug={activeSlug}
                    onSelect={onSelectSource}
                  />
                )}
              </div>
            ),
          )
        )}

        {isThinking && (
          <output className="flex gap-1.5 pl-1 pt-1" aria-label="Thinking">
            <span className="h-2 w-2 animate-bounce rounded-full bg-blue-3 [animation-delay:-0.3s]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-blue-3 [animation-delay:-0.15s]" />
            <span className="h-2 w-2 animate-bounce rounded-full bg-blue-3" />
          </output>
        )}

        {error && (
          <p className="rounded-xl bg-red-50 px-3.5 py-2.5 text-sm text-red-700 text-pretty">
            Something went wrong reaching help. Try again in a moment.
          </p>
        )}
      </div>

      {hasConversation && (
        <div className="shrink-0 border-t border-border p-3">
          <form
            onSubmit={(event) => {
              event.preventDefault()
              submit(input)
            }}
            className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-1.5 focus-within:border-brand/50"
          >
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask a follow-up…"
              className="flex-1 bg-transparent text-[0.94rem] text-blue-7 outline-none placeholder:text-blue-4"
            />
            <button
              type="submit"
              disabled={!input.trim() || isBusy}
              aria-label="Send"
              className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-brand text-white transition-opacity disabled:cursor-default disabled:opacity-40"
            >
              <SendHorizontalIcon className="h-4 w-4" />
            </button>
          </form>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={onStartOver}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-white px-2.5 py-1.5 text-xs font-medium text-blue-6 transition-colors hover:border-brand/40 hover:bg-brand/5 hover:text-brand"
            >
              <RotateCcwIcon className="h-3.5 w-3.5" />
              Start a new question
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
